import React from 'react';
import { UserWithRole, UserIpHistory } from '../../../../../shared/services/userService';
import LoadingSpinner from '../../../../../shared/components/LoadingSpinner';

type TFn = (key: string) => string;

const sectionCls = 'rounded-lg border border-[#223046] bg-[#0b1220]/80 p-4';
const labelCls = 'text-xs text-cockpit-text-2';
const valueCls = 'text-cockpit-text font-medium text-sm';

export function getRoleBadgeColor(roleId: number): string {
  switch (roleId) {
    case 1: return 'bg-red-500/20 text-red-300 border-red-500/40';
    case 2: return 'bg-orange-500/20 text-orange-300 border-orange-500/40';
    case 3: return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
    case 4: return 'bg-cockpit-violet/20 text-cockpit-violet border-cockpit-violet/40';
    case 5: return 'bg-blue-500/20 text-blue-300 border-blue-500/40';
    case 6: return 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40';
    default: return 'bg-slate-500/20 text-cockpit-text-2 border-slate-500/40';
  }
}

interface BasicInfoProps { user: UserWithRole; t: TFn; }

export const BasicInfoSection: React.FC<BasicInfoProps> = ({ user, t }) => (
  <div className={sectionCls}>
    <h4 className="text-sm font-semibold text-cockpit-text mb-3">{t('details.basicInfo')}</h4>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>{t('details.id')}</label>
        <p className={valueCls}>#{user.id}</p>
      </div>
      <div>
        <label className={labelCls}>{t('details.username')}</label>
        <p className={valueCls}>{user.username}</p>
      </div>
      <div>
        <label className={labelCls}>{t('details.email')}</label>
        <p className={valueCls}>{user.email}</p>
      </div>
      <div>
        <label className={labelCls}>{t('details.role')}</label>
        <div className="mt-1">
          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getRoleBadgeColor(user.roles_id)}`}>
            {user.role_name || `Rôle ${user.roles_id}`}
          </span>
          {user.role_description && (
            <p className="text-cockpit-text-2 text-xs mt-1">{user.role_description}</p>
          )}
        </div>
      </div>
      <div>
        <label className={labelCls}>{t('details.status')}</label>
        <div className="mt-1">
          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${
            user.activated
              ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
              : 'bg-red-500/20 text-red-300 border-red-500/40'
          }`}>
            {user.activated ? t('status.active') : t('status.inactive')}
          </span>
        </div>
      </div>
      {user.last_username_change && (
        <div>
          <label className={labelCls}>{t('details.lastUsernameChange')}</label>
          <p className={valueCls}>{new Date(user.last_username_change).toLocaleString('fr-FR')}</p>
        </div>
      )}
    </div>
  </div>
);

export const AccountInfoSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => (
  <div className={sectionCls}>
    <h4 className="text-sm font-semibold text-cockpit-text mb-3">{t('details.accountInfo')}</h4>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>{t('details.accountCreated')}</label>
        <p className={valueCls}>{user.created_at ? new Date(user.created_at).toLocaleString('fr-FR') : '-'}</p>
      </div>
      {user.updated_at && (
        <div>
          <label className={labelCls}>{t('details.lastUpdated')}</label>
          <p className={valueCls}>{new Date(user.updated_at).toLocaleString('fr-FR')}</p>
        </div>
      )}
      <div>
        <label className={labelCls}>{t('details.lastLogin')}</label>
        <p className={valueCls}>{user.last_login ? new Date(user.last_login).toLocaleString('fr-FR') : t('details.never')}</p>
      </div>
      <div>
        <label className={labelCls}>{t('details.lastActivity')}</label>
        <p className={valueCls}>{user.last_activity ? new Date(user.last_activity).toLocaleString('fr-FR') : t('details.never')}</p>
      </div>
    </div>
  </div>
);

export const PremiumInfoSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => (
  <div className={sectionCls}>
    <h4 className="text-sm font-semibold text-cockpit-text mb-3">{t('details.premiumInfo')}</h4>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>{t('details.premium')}</label>
        <p className={valueCls}>
          {user.premium
            ? <span className="text-amber-300">{t('details.yes')}</span>
            : <span className="text-cockpit-text-2">{t('details.no')}</span>}
        </p>
      </div>
      {user.premium_type !== undefined && (
        <div>
          <label className={labelCls}>{t('details.premiumType')}</label>
          <p className={valueCls}>{user.premium_type || '-'}</p>
        </div>
      )}
      {user.premium_start && (
        <div>
          <label className={labelCls}>{t('details.premiumStart')}</label>
          <p className={valueCls}>{new Date(user.premium_start).toLocaleDateString('fr-FR')}</p>
        </div>
      )}
      {user.premium_end && (
        <div>
          <label className={labelCls}>{t('details.premiumEnd')}</label>
          <p className={valueCls}>{new Date(user.premium_end).toLocaleDateString('fr-FR')}</p>
        </div>
      )}
      {user.premium_until && (
        <div>
          <label className={labelCls}>{t('details.until')}</label>
          <p className={valueCls}>{new Date(user.premium_until).toLocaleDateString('fr-FR')}</p>
        </div>
      )}
    </div>
  </div>
);

export const GameInfoSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => (
  <div className={sectionCls}>
    <h4 className="text-sm font-semibold text-cockpit-text mb-3">{t('details.gameInfo')}</h4>
    <div className="grid grid-cols-2 gap-3">
      {user.xp !== undefined && (
        <div>
          <label className={labelCls}>{t('details.xp')}</label>
          <p className={valueCls}>{user.xp?.toLocaleString('fr-FR') || 0}</p>
        </div>
      )}
      {user.niveau !== undefined && (
        <div>
          <label className={labelCls}>{t('details.level')}</label>
          <p className={valueCls}>{user.niveau || 1}</p>
        </div>
      )}
      {user.departement_code && (
        <div>
          <label className={labelCls}>{t('details.department')}</label>
          <p className={valueCls}>{user.department || user.departement_code}</p>
        </div>
      )}
      {user.argents !== undefined && (
        <div>
          <label className={labelCls}>{t('details.money')}</label>
          <p className={valueCls}>{user.argents?.toLocaleString('fr-FR') || 0}</p>
        </div>
      )}
      {user.medailles !== undefined && (
        <div>
          <label className={labelCls}>{t('details.medals')}</label>
          <p className={valueCls}>{user.medailles?.toLocaleString('fr-FR') || 0}</p>
        </div>
      )}
      {user.batiments !== undefined && (
        <div>
          <label className={labelCls}>{t('details.buildings')}</label>
          <p className={valueCls}>{user.batiments || 0}</p>
        </div>
      )}
      {user.vehicules !== undefined && (
        <div>
          <label className={labelCls}>{t('details.vehicles')}</label>
          <p className={valueCls}>{user.vehicules || 0}</p>
        </div>
      )}
    </div>
    {user.statistiques && (
      <div className="mt-4 pt-4 border-t border-[#1a2537]">
        <h5 className="text-sm font-medium text-cockpit-text-2 mb-3">{t('details.statistics')}</h5>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={labelCls}>{t('details.statBuildings')}</label><p className={valueCls}>{user.statistiques.batiments || 0}</p></div>
          <div><label className={labelCls}>{t('details.statVehicles')}</label><p className={valueCls}>{user.statistiques.vehicules || 0}</p></div>
          <div><label className={labelCls}>{t('details.statInterventions')}</label><p className={valueCls}>{user.statistiques.interventions || 0}</p></div>
          <div><label className={labelCls}>{t('details.statTickets')}</label><p className={valueCls}>{user.statistiques.tickets_support || 0}</p></div>
          <div><label className={labelCls}>{t('details.statFriends')}</label><p className={valueCls}>{user.statistiques.amis || 0}</p></div>
          <div><label className={labelCls}>{t('details.statPersonnel')}</label><p className={valueCls}>{user.statistiques.personnel || 0}</p></div>
        </div>
      </div>
    )}
  </div>
);

interface SecurityInfoProps {
  user: UserWithRole;
  ipHistory: UserIpHistory[];
  loadingIpHistory: boolean;
  t: TFn;
}

export const SecurityInfoSection: React.FC<SecurityInfoProps> = ({ user, ipHistory, loadingIpHistory, t }) => (
  <div className={sectionCls}>
    <h4 className="text-sm font-semibold text-cockpit-text mb-3">{t('details.securityInfo')}</h4>
    <div className="grid grid-cols-2 gap-3">
      <div>
        <label className={labelCls}>{t('details.currentIp')}</label>
        <p className={valueCls}>{user.last_ip || <span className="text-cockpit-muted">{t('details.notAvailable')}</span>}</p>
      </div>
      {user.failed_attempts !== undefined && (
        <div>
          <label className={labelCls}>{t('details.failedAttempts')}</label>
          <p className={valueCls}>{user.failed_attempts || 0}</p>
        </div>
      )}
      {user.last_failed_attempt && (
        <div>
          <label className={labelCls}>{t('details.lastFailedAttempt')}</label>
          <p className={valueCls}>{new Date(user.last_failed_attempt).toLocaleString('fr-FR')}</p>
        </div>
      )}
      {user.bann !== undefined && (
        <div>
          <label className={labelCls}>{t('details.banStatus')}</label>
          <p className={valueCls}>
            {user.bann === 0
              ? <span className="text-emerald-400">{t('details.notBanned')}</span>
              : <span className="text-red-400">{t('details.banned')}</span>}
          </p>
        </div>
      )}
      {user.ban_end && (
        <div>
          <label className={labelCls}>{t('details.banEnd')}</label>
          <p className={valueCls}>{new Date(user.ban_end).toLocaleString('fr-FR')}</p>
        </div>
      )}
      {user.ban_reason && (
        <div className="col-span-2">
          <label className={labelCls}>{t('details.banReason')}</label>
          <p className={valueCls}>{user.ban_reason}</p>
        </div>
      )}
      {user.muted && (
        <div>
          <label className={labelCls}>{t('details.muted')}</label>
          <p className={valueCls}>
            {user.muted
              ? <span className="text-red-400">{t('details.yes')}</span>
              : <span className="text-emerald-400">{t('details.no')}</span>}
          </p>
        </div>
      )}
      {user.mute_end && (
        <div>
          <label className={labelCls}>{t('details.muteEnd')}</label>
          <p className={valueCls}>{new Date(user.mute_end).toLocaleString('fr-FR')}</p>
        </div>
      )}
      {user.mute_reason && (
        <div className="col-span-2">
          <label className={labelCls}>{t('details.muteReason')}</label>
          <p className={valueCls}>{user.mute_reason}</p>
        </div>
      )}
      {user.banned_ips_id && (
        <div>
          <label className={labelCls}>{t('details.bannedIpId')}</label>
          <p className={valueCls}>#{user.banned_ips_id}</p>
        </div>
      )}
    </div>

    <div className="mt-4 pt-4 border-t border-[#1a2537]">
      <h5 className="text-sm font-medium text-cockpit-text-2 mb-3">{t('details.ipHistory')}</h5>
      {loadingIpHistory ? (
        <div className="flex justify-center py-4"><LoadingSpinner /></div>
      ) : ipHistory.length > 0 ? (
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {ipHistory.map((ipRecord) => (
            <div
              key={ipRecord.id}
              className={`rounded-lg border p-3 ${
                ipRecord.ip_address === user.last_ip
                  ? 'bg-blue-500/10 border-blue-500/40'
                  : 'border-[#223046] bg-[#0b1220]/60'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="text-cockpit-text text-sm font-medium">
                    {ipRecord.ip_address}
                    {ipRecord.ip_address === user.last_ip && (
                      <span className="ml-2 px-2 py-0.5 rounded text-xs bg-blue-500/20 text-blue-300 border border-blue-500/40">
                        {t('details.current')}
                      </span>
                    )}
                  </p>
                  <div className="flex gap-4 mt-1 text-xs text-cockpit-text-2">
                    <span>{t('details.firstSeen')}: {new Date(ipRecord.first_seen).toLocaleString('fr-FR')}</span>
                    <span>{t('details.lastSeen')}: {new Date(ipRecord.last_seen).toLocaleString('fr-FR')}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-cockpit-text-2 text-sm">{t('details.noIpHistory')}</p>
      )}
    </div>
  </div>
);

export const PreferencesSection: React.FC<{ user: UserWithRole; t: TFn }> = ({ user, t }) => {
  const hasPrefs =
    user.email_notifications !== undefined ||
    user.push_notifications !== undefined ||
    user.newsletter !== undefined ||
    user.profile_visibility !== undefined ||
    user.show_email !== undefined ||
    user.show_stats !== undefined;

  if (!hasPrefs) return null;

  const yesSpan = (label: string) => <span className="text-emerald-400">{label}</span>;
  const noSpan = (label: string) => <span className="text-cockpit-text-2">{label}</span>;

  return (
    <div className={sectionCls}>
      <h4 className="text-sm font-semibold text-cockpit-text mb-3">{t('details.preferences')}</h4>
      <div className="grid grid-cols-2 gap-3">
        {user.email_notifications !== undefined && (
          <div>
            <label className={labelCls}>{t('details.emailNotifications')}</label>
            <p className={valueCls}>{user.email_notifications ? yesSpan(t('details.enabled')) : noSpan(t('details.disabled'))}</p>
          </div>
        )}
        {user.push_notifications !== undefined && (
          <div>
            <label className={labelCls}>{t('details.pushNotifications')}</label>
            <p className={valueCls}>{user.push_notifications ? yesSpan(t('details.enabled')) : noSpan(t('details.disabled'))}</p>
          </div>
        )}
        {user.newsletter !== undefined && (
          <div>
            <label className={labelCls}>{t('details.newsletter')}</label>
            <p className={valueCls}>{user.newsletter ? yesSpan(t('details.yes')) : noSpan(t('details.no'))}</p>
          </div>
        )}
        {user.profile_visibility && (
          <div>
            <label className={labelCls}>{t('details.profileVisibility')}</label>
            <p className={valueCls}>{user.profile_visibility}</p>
          </div>
        )}
        {user.show_email !== undefined && (
          <div>
            <label className={labelCls}>{t('details.showEmail')}</label>
            <p className={valueCls}>{user.show_email ? yesSpan(t('details.yes')) : noSpan(t('details.no'))}</p>
          </div>
        )}
        {user.show_stats !== undefined && (
          <div>
            <label className={labelCls}>{t('details.showStats')}</label>
            <p className={valueCls}>{user.show_stats ? yesSpan(t('details.yes')) : noSpan(t('details.no'))}</p>
          </div>
        )}
      </div>
    </div>
  );
};
